import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Product, Connection, Interface } from '../../types/productIntegration';
import { SAMPLE_PRODUCTS, SAMPLE_CONNECTIONS, getConnectionsForProduct, getProductById, getAllInterfaces, getInterfaceById, getActiveInterfaces, getProductsConnectedByInterface, getProductIcon } from '../../data/integrationData';
import './IntegrationVisualization.css';
import { useDarkTheme } from '../../contexts/DarkThemeContext';

interface NodeData extends d3.SimulationNodeDatum {
  id: string;
  product: Product;
  expanded: boolean;
  connections: Connection[];
  level: number;
  parentId?: string; // Track which node caused this node to be added
}

interface LinkData extends d3.SimulationLinkDatum<NodeData> {
  connection: Connection;
  source: NodeData;
  target: NodeData;
}

const IntegrationVisualization: React.FC = () => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [simulation, setSimulation] = useState<d3.Simulation<NodeData, LinkData> | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTeam, setSelectedTeam] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const isExpandingRef = useRef<boolean>(false); // Prevent double expansion calls using ref for immediate access
  const [nodes, setNodes] = useState<NodeData[]>([]);
  const [links, setLinks] = useState<LinkData[]>([]);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [showInstructions, setShowInstructions] = useState<boolean>(true);
  
  // Interface panel state
  const [showProductsPanel, setShowProductsPanel] = useState<boolean>(true);
  const [showInterfacesPanel, setShowInterfacesPanel] = useState<boolean>(false);
  const [selectedInterface, setSelectedInterface] = useState<Interface | null>(null);
  const [interfaceSearchTerm, setInterfaceSearchTerm] = useState<string>('');
  const [selectedProtocol, setSelectedProtocol] = useState<string>('all');
  const [selectedInterfaceStatus, setSelectedInterfaceStatus] = useState<string>('all');

  // Dark theme support
  const { isDarkTheme } = useDarkTheme();
  const svgTextColor = isDarkTheme ? '#e0e0e0' : '#333';
  const svgSubTextColor = isDarkTheme ? '#a0a0b0' : '#666';
  const svgLinkColor = isDarkTheme ? '#555' : '#999999';
  const svgInterfaceBgColor = isDarkTheme ? '#142737' : 'white';
  const svgInterfaceStrokeColor = isDarkTheme ? '#28b1ec' : '#007acc';
  const svgSidebarResetBg = isDarkTheme ? '#001526' : '#fff';
  const svgSidebarDragBg = isDarkTheme ? '#3a1a1a' : '#ffe6e6';

  // Helper function to get direction icon and text
  const getDirectionDisplay = (direction: string) => {
    switch (direction) {
      case 'inbound':
        return { icon: '←', text: 'INBOUND' };
      case 'outbound':
        return { icon: '→', text: 'OUTBOUND' };
      case 'bidirectional':
        return { icon: '↔', text: 'BIDIRECTIONAL' };
      default:
        return { icon: '↔', text: direction.toUpperCase() };
    }
  };

  // Helper function to get shortened protocol name
  const getProtocolDisplay = (protocol: string) => {
    switch (protocol.toUpperCase()) {
      case 'REST API':
        return 'REST';
      case 'GRAPHQL':
        return 'GRAPHQL';
      case 'WEBSOCKET':
        return 'WEBSOCKET';
      case 'MESSAGE QUEUE':
        return 'MESSAGE QUEUE';
      default:
        return protocol.length > 8 ? protocol.substring(0, 8) : protocol;
    }
  };

  // Get unique categories, teams, and types for filtering
  const categories = ['all', ...Array.from(new Set([
    ...SAMPLE_PRODUCTS.map(p => p.category),
    ...getAllInterfaces().flatMap(i => i.categories || [])
  ]))];
  const teams = ['all', ...Array.from(new Set([
    ...SAMPLE_PRODUCTS.map(p => p.team),
    ...getAllInterfaces().map(i => i.team).filter(Boolean)
  ]))];
  const types = ['all', 'internal', 'external', ...Array.from(new Set(getAllInterfaces().map(i => i.type).filter(Boolean)))];
  
  // Get unique protocols and status for interface filtering
  const protocols = ['all', ...Array.from(new Set(getAllInterfaces().map(i => i.protocol)))];
  const interfaceStatuses = ['all', 'active', 'planned', 'deprecated'];

  // Filter products based on search, category, team, and type
  const filteredProducts = SAMPLE_PRODUCTS.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.team.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesTeam = selectedTeam === 'all' || product.team === selectedTeam;
    const matchesType = selectedType === 'all' || product.type === selectedType;
    return matchesSearch && matchesCategory && matchesTeam && matchesType;
  }).sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically by name

  // Filter interfaces based on search and all filter criteria
  const filteredInterfaces = getAllInterfaces().filter(iface => {
    const matchesSearch = iface.name.toLowerCase().includes(interfaceSearchTerm.toLowerCase()) ||
                         (iface.description?.toLowerCase().includes(interfaceSearchTerm.toLowerCase()) ?? false);
    const matchesProtocol = selectedProtocol === 'all' || iface.protocol === selectedProtocol;
    const matchesCategory = selectedCategory === 'all' || 
                           (iface.categories && iface.categories.includes(selectedCategory));
    const matchesTeam = selectedTeam === 'all' || iface.team === selectedTeam;
    const matchesType = selectedType === 'all' || iface.type === selectedType;
    const matchesStatus = selectedInterfaceStatus === 'all' || iface.status === selectedInterfaceStatus;
    
    return matchesSearch && matchesProtocol && matchesCategory && matchesTeam && 
           matchesType && matchesStatus;
  }).sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically by name

  // Apply filters to visualization data as well
  const getFilteredVisualizationData = () => {
    const filteredProductIds = new Set(filteredProducts.map(p => p.id));
    
    // Don't force include core nodes - respect the user's filters
    // This allows for better filtering behavior
    return {
      products: SAMPLE_PRODUCTS.filter(p => filteredProductIds.has(p.id)),
      connections: SAMPLE_CONNECTIONS.filter(c => 
        filteredProductIds.has(c.sourceProductId) && filteredProductIds.has(c.targetProductId)
      )
    };
  };

  // Function to remove a node and its connections from the visualization
  const removeNodeFromVisualization = (nodeId: string) => {
    if (!simulation) return;
    
    console.log(`🗑️ Removing node: ${nodeId}`);
    
    const currentNodes = simulation.nodes();
    const currentLinks = simulation.force<d3.ForceLink<NodeData, LinkData>>('link')?.links() || [];
    
    // Remove the node from the nodes array
    const updatedNodes = currentNodes.filter(n => n.id !== nodeId);
    
    // Remove all links connected to this node
    const updatedLinks = currentLinks.filter(l => 
      (l.source as NodeData).id !== nodeId && (l.target as NodeData).id !== nodeId
    );
    
    // Remove from expanded nodes if it was expanded
    const newExpandedNodes = new Set(expandedNodes);
    newExpandedNodes.delete(nodeId);
    setExpandedNodes(newExpandedNodes);
    
    // Update simulation
    simulation.nodes(updatedNodes);
    simulation.force('link', d3.forceLink<NodeData, LinkData>(updatedLinks)
      .id(d => d.id)
      .distance(150)
      .strength(0.5));
    
    // Restart simulation with gentle movement
    simulation.alpha(0.3).restart();
    
    console.log(`🗑️ Node removed. Remaining nodes: ${updatedNodes.length}, links: ${updatedLinks.length}`);
  };

  // Effect to handle expand state changes (UI indicators only)
  useEffect(() => {
    if (!simulation) return;
    
    console.log(`🔄 Updating expand indicators...`);
    console.log(`Current expanded nodes:`, Array.from(expandedNodes));
    
    // Update the expand indicators for all nodes
    d3.select(svgRef.current)
      .selectAll<SVGGElement, NodeData>('.node')
      .select('.expand-indicator')
      .attr('fill', d => expandedNodes.has(d.id) ? '#dc3545' : '#007acc');
      
    d3.select(svgRef.current)
      .selectAll<SVGGElement, NodeData>('.node')
      .select('.expand-icon')
      .text(d => expandedNodes.has(d.id) ? '−' : '+');
      
  }, [expandedNodes, simulation]);

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    
    // Get the actual container dimensions
    const container = svgRef.current.parentElement;
    const containerRect = container?.getBoundingClientRect();
    const width = containerRect?.width || 1200;
    const height = Math.max(600, containerRect?.height || 800); // Min height of 600px

    console.log(`📏 Canvas dimensions: ${width}x${height}`);

    // Update SVG dimensions and viewBox to match container
    svg.attr('width', width)
       .attr('height', height)
       .attr('viewBox', `0 0 ${width} ${height}`);

    // Clear previous content
    svg.selectAll('*').remove();

    // Create main group for zooming/panning
    const g = svg.append('g');

    // Add zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    // Create definitions for gradients and markers
    const defs = svg.append('defs');
    
    // Create radial gradients for node bubbles
    // Internal nodes gradient (green) - smooth subtle transition from transparent center to green edges
    const internalGradient = defs.append('radialGradient')
      .attr('id', 'internal-gradient')
      .attr('cx', '50%')
      .attr('cy', '50%')
      .attr('r', '50%');
    
    internalGradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#28a745')
      .attr('stop-opacity', '0'); // Transparent center
    
    internalGradient.append('stop')
      .attr('offset', '70%')
      .attr('stop-color', '#28a745')
      .attr('stop-opacity', '0.1'); // Very light in the middle
    
    internalGradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#28a745')
      .attr('stop-opacity', '0.3'); // More visible at the edges
    
    // External nodes gradient (yellow) - smooth subtle transition from transparent center to yellow edges
    const externalGradient = defs.append('radialGradient')
      .attr('id', 'external-gradient')
      .attr('cx', '50%')
      .attr('cy', '50%')
      .attr('r', '50%');
    
    externalGradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#ffc107')
      .attr('stop-opacity', '0'); // Transparent center
    
    externalGradient.append('stop')
      .attr('offset', '70%')
      .attr('stop-color', '#ffc107')
      .attr('stop-opacity', '0.1'); // Very light in the middle
    
    externalGradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#ffc107')
      .attr('stop-opacity', '0.3'); // More visible at the edges

    // Get filtered data for visualization
    const filteredData = getFilteredVisualizationData();
    
    // Start with EMPTY visualization - let users choose what to display
    const initialNodes: NodeData[] = [];
    const initialLinks: LinkData[] = [];

    // Initialize state
    setNodes(initialNodes);
    setLinks(initialLinks);

    // Create simulation with improved collision detection and spacing
    const newSimulation = d3.forceSimulation<NodeData, LinkData>(initialNodes)
      .force('link', d3.forceLink<NodeData, LinkData>(initialLinks)
        .id(d => d.id)
        .distance(250) // Match our expansion distance
        .strength(0.05) // Even weaker strength to minimize movement while allowing collision avoidance
      )
      .force('charge', d3.forceManyBody().strength(-1200)) // Increased repulsion for better collision avoidance
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(120)) // Increased collision radius for better spacing
      .force('x', d3.forceX(width / 2).strength(0.005)) // Even weaker centering force
      .force('y', d3.forceY(height / 2).strength(0.005)) // Even weaker centering force
      .alpha(0.01)  // Very low initial energy
      .alphaDecay(0.02)  // Slow decay
      .alphaMin(0.001); // Low minimum alpha

    setSimulation(newSimulation);

    // Stop the simulation after initial positioning
    newSimulation.on('end', () => {
      // Release fixed positions after initial layout
      initialNodes.forEach(node => {
        node.fx = null;
        node.fy = null;
      });
    });

    // Create links first (so they appear behind nodes)
    const linkGroup = g.append('g').attr('class', 'links');
    // Create nodes second (so they appear in front of links)  
    const nodeGroup = g.append('g').attr('class', 'nodes');
    
    // Ensure proper z-order by explicitly setting the groups in correct order
    console.log(`🔍 Group creation order: Links first (background), then Nodes (foreground)`);
    console.log(`🔍 Links group:`, linkGroup.node());
    console.log(`🔍 Nodes group:`, nodeGroup.node());

    // Store references for updates
    (newSimulation as any).linkGroup = linkGroup;
    (newSimulation as any).nodeGroup = nodeGroup;
    (newSimulation as any).width = width;
    (newSimulation as any).height = height;
    (newSimulation as any).zoom = zoom;
    (newSimulation as any).svg = svg;
    (newSimulation as any).g = g;

    // Function to center a node in the viewport
    const focusNodeInViewport = (node: NodeData, duration?: number) => {
      if (!node || !node.x || !node.y) {
        return;
      }
      // Calculate the center position
      const centerX = width / 2;
      const centerY = height / 2;
      
      // Get current transform
      const currentTransform = d3.zoomTransform(svg.node() as Element);
      
      const renderedNodeX = node.x * currentTransform.k + currentTransform.x;
      const renderedNodeY = node.y * currentTransform.k + currentTransform.y;
      const deltaX = centerX - renderedNodeX;
      const deltaY = centerY - renderedNodeY;
      const distanceFromCenter = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // Calculates duration based on how far the node is from the viewport center, clamping the result between 250 ms and 1200 ms to keep the zoom animation smooth.
      const easingDuration = duration ?? Math.max(250, Math.min(1200, 250 + distanceFromCenter * 0.4));

      // Calculate the translation needed to center the node
      const translateX = centerX - node.x * currentTransform.k;
      const translateY = centerY - node.y * currentTransform.k;
      
      // Apply smooth transition to center the node
      svg.transition()
        .duration(easingDuration)
        .call(
          zoom.transform as any,
          d3.zoomIdentity
            .translate(translateX, translateY)
            .scale(currentTransform.k)
        );
    };

  // Store focusNodeInViewport function for access in expandNode
  (newSimulation as any).focusNodeInViewport = focusNodeInViewport;

    const updateVisualization = (currentExpandedNodes?: Set<string>) => {
      console.log(`📊 UPDATE VISUALIZATION CALLED`);
      const expandedNodesSet = currentExpandedNodes || expandedNodes;
      
      console.log(`🔍 DOM order check - Links group created first, Nodes group created second`);
      console.log(`🔍 Links should appear behind nodes due to SVG rendering order`);
      
      // Update links
      const currentLinks = newSimulation.force<d3.ForceLink<NodeData, LinkData>>('link')?.links() || [];
      console.log(`Current links in simulation:`, currentLinks.length);
      
      const links = linkGroup.selectAll<SVGGElement, LinkData>('.link-group')
        .data(currentLinks, d => `${(d.source as NodeData).id}-${(d.target as NodeData).id}`);

      console.log(`Links to remove:`, links.exit().size());
      links.exit().remove();

      const linkEnter = links.enter().append('g')
        .attr('class', 'link-group');

      // Ensure links stay in background - will be handled after nodes are created

      // Add the connection line (without arrows) - ensure it stays behind nodes
      linkEnter.append('line')
        .attr('class', 'link')
        .attr('stroke-width', 1) // Further reduced for subtler lines
        .attr('stroke', svgLinkColor) // Theme-aware connector color
        .attr('stroke-opacity', 0.4) // Much reduced opacity to ensure they stay in background
        .style('pointer-events', 'none'); // Prevent interaction so nodes are always clickable

      // Add interface indicators for external-internal connections
      const interfaceGroups = linkEnter
        .filter(d => {
          const sourceProduct = SAMPLE_PRODUCTS.find(p => p.id === (d.source as NodeData).id);
          const targetProduct = SAMPLE_PRODUCTS.find(p => p.id === (d.target as NodeData).id);
          return !!(d.connection.interface && 
                   ((sourceProduct?.type === 'external' && targetProduct?.type === 'internal') ||
                    (sourceProduct?.type === 'internal' && targetProduct?.type === 'external')));
        })
        .append('g')
        .attr('class', 'interface-indicator')
        .style('cursor', 'pointer');

      // Add interface background circle (outlined, not filled)
      interfaceGroups
        .append('circle')
        .attr('r', 15) // Increased from 12 to 15 for bigger circle
        .attr('fill', svgInterfaceBgColor)
        .attr('stroke', svgInterfaceStrokeColor)
        .attr('stroke-width', 2);

      // Add interface icon (interface.png image)
      interfaceGroups
        .append('image')
        .attr('href', '/icons/interface.png')
        .attr('x', -15) // Center the 30px icon (increased from 24px)
        .attr('y', -15)
        .attr('width', 30) // Increased from 24 to 30
        .attr('height', 30) // Increased from 24 to 30
        .style('pointer-events', 'none')
        .style('filter', isDarkTheme ? 'invert(1) brightness(0.9)' : 'none')
        .on('error', function() {
          console.error('❌ Failed to load interface icon: /icons/interface.png');
          // Fallback to text "I" if image fails to load
          const parentElement = this.parentNode as SVGGElement;
          if (parentElement) {
            d3.select(parentElement).append('text')
              .attr('text-anchor', 'middle')
              .attr('dy', '0.35em')
              .attr('fill', svgInterfaceStrokeColor) // Theme-aware text to match the outline
              .attr('font-size', '14px') // Slightly larger text
              .attr('font-weight', 'bold')
              .text('I');
          }
        });

      // Add interface label below the indicator
      interfaceGroups
        .append('text')
        .attr('class', 'interface-label')
        .attr('text-anchor', 'middle')
        .attr('y', 20)
        .attr('fill', svgInterfaceStrokeColor)
        .attr('font-size', '10px')
        .attr('font-weight', 'bold')
        .style('pointer-events', 'none')
        .text(d => d.connection.interface?.name || 'Interface');

      // Add click handler for interface details
      interfaceGroups.on('click', (event, d) => {
        event.stopPropagation();
        if (d.connection.interface) {
          // Set the interface connection as selected to show its details
          const sourceProduct = SAMPLE_PRODUCTS.find(p => p.id === (d.source as NodeData).id);
          const targetProduct = SAMPLE_PRODUCTS.find(p => p.id === (d.target as NodeData).id);
          
          // Create a pseudo-product that represents the interface for detail display
          const interfaceProduct: Product = {
            id: d.connection.interface.id,
            name: d.connection.interface.name,
            category: 'Integration Interface',
            team: `${sourceProduct?.team || 'Unknown'} & ${targetProduct?.team || 'Unknown'}`,
            type: 'internal', // This field will be hidden for interfaces
            status: d.connection.interface.status === 'planned' ? 'development' : d.connection.interface.status as 'active' | 'deprecated' | 'development',
            productContact: `${sourceProduct?.name} ↔ ${targetProduct?.name}`,
            engineeringContact: d.connection.interface.description || 'Interface integration between systems',
            // Store interface details for easy access
            interfaceDetails: {
              protocol: d.connection.interface.protocol,
              direction: d.connection.interface.direction,
              authentication: d.connection.interface.authentication,
              dataFormat: d.connection.interface.dataFormat,
              technicalDocumentationUrl: d.connection.interface.technicalDocumentationUrl
            }
          };
          
          setSelectedProduct(interfaceProduct);
        }
      });

      console.log(`New links added:`, linkEnter.size());

      // Update nodes
      const currentNodes = newSimulation.nodes();
      console.log(`Current nodes in simulation:`, currentNodes.length, currentNodes.map(n => n.product.name));
      
      const nodes = nodeGroup.selectAll<SVGGElement, NodeData>('.node')
        .data(currentNodes, d => d.id);

      console.log(`Nodes to remove:`, nodes.exit().size());
      nodes.exit().remove();

      const nodeEnter = nodes.enter().append('g')
        .attr('class', d => currentNodes.length === 1 ? 'node first-node' : 'node')
        .call(d3.drag<SVGGElement, NodeData>()
          .on('start', (event, d) => {
            if (!event.active) newSimulation.alphaTarget(0.1).restart();
            d.fx = d.x;
            d.fy = d.y;
            console.log(`🖱️ Started dragging: ${d.product.name} - released from fixed position`);
          })
          .on('drag', (event, d) => {
            d.fx = event.x;
            d.fy = event.y;
            
            // Visual feedback for drag-to-remove: highlight sidebar when node is over it
            const sidebarElement = document.querySelector('.product-sidebar') as HTMLElement;
            if (sidebarElement) {
              const sidebarRect = sidebarElement.getBoundingClientRect();
              const svgElement = svgRef.current;
              if (svgElement) {
                const svgRect = svgElement.getBoundingClientRect();
                const mouseX = event.sourceEvent.clientX - svgRect.left;
                const mouseY = event.sourceEvent.clientY - svgRect.top;
                
                // Check if mouse is over sidebar area (accounting for SVG offset)
                const isOverSidebar = event.sourceEvent.clientX < sidebarRect.right;
                
                if (isOverSidebar) {
                  sidebarElement.style.backgroundColor = svgSidebarDragBg; // Theme-aware red tint
                  sidebarElement.style.transition = 'background-color 0.2s ease';
                } else {
                  sidebarElement.style.backgroundColor = svgSidebarResetBg;
                }
              }
            }
          })
          .on('end', (event, d) => {
            if (!event.active) newSimulation.alphaTarget(0);
            
            // Check if node was dropped over sidebar to remove it
            const sidebarElement = document.querySelector('.product-sidebar') as HTMLElement;
            if (sidebarElement) {
              const sidebarRect = sidebarElement.getBoundingClientRect();
              const isOverSidebar = event.sourceEvent.clientX < sidebarRect.right;
              
              // Reset sidebar styling
              sidebarElement.style.backgroundColor = svgSidebarResetBg;
              
              if (isOverSidebar) {
                // Remove the node and its connections
                removeNodeFromVisualization(d.id);
                return; // Don't release position since node is being removed
              }
            }
            
            // Keep the node fixed at the new position after dragging
            // d.fx and d.fy remain set to maintain the dragged position
            console.log(`🖱️ Finished dragging: ${d.product.name} - keeping fixed at new position (${d.fx}, ${d.fy})`);
          })
        );

      console.log(`New nodes added:`, nodeEnter.size());

      // Add circles for nodes with gradient fills
      nodeEnter.append('circle')
        .attr('r', 40) // Reduced from 50 to 40 for smaller nodes
        .attr('fill', d => d.product.type === 'internal' ? 'url(#internal-gradient)' : 'url(#external-gradient)')
        .attr('stroke', d => d.product.type === 'internal' ? '#28a745' : '#ffc107')
        .attr('stroke-width', 3) // Reduced from 4 to 3 for thinner stroke
        .style('cursor', 'pointer');

      // Add product icons inside the circles
      nodeEnter.append('image')
        .attr('href', d => {
          const iconPath = `/icons/${getProductIcon(d.product)}`;
          console.log(`🖼️ Loading icon for ${d.product.name}: ${iconPath}`);
          return iconPath;
        })
        .attr('x', -25) // Center the 50px icon (reduced from 60px)
        .attr('y', -25)
        .attr('width', 50) // Reduced from 60 to 50
        .attr('height', 50) // Reduced from 60 to 50
        .style('cursor', 'pointer')
        .style('pointer-events', 'none') // Let click events pass through to the circle
        .style('filter', isDarkTheme ? 'invert(1) brightness(0.9)' : 'none')
        .on('error', function(event, d) {
          console.error(`❌ Failed to load icon for ${d.product.name}: /icons/${getProductIcon(d.product)}`);
          // Replace with a fallback colored circle if image fails to load
          const parentElement = this.parentNode as SVGGElement;
          if (parentElement) {
            d3.select(parentElement).append('circle')
              .attr('r', 25)
              .attr('fill', '#007acc')
              .attr('stroke', 'white')
              .attr('stroke-width', 2);
          }
        })
        .on('load', function(event, d) {
          console.log(`✅ Successfully loaded icon for ${d.product.name}`);
        });

      // Helper function to check if a node should have an expand/collapse indicator
      const shouldHaveExpandIndicator = (nodeData: NodeData) => {
        // If node is currently expanded, it should always have a collapse indicator
        if (expandedNodesSet.has(nodeData.id)) {
          return true;
        }
        
        // Check if node has expandable connections (not already visible nodes)
        const filteredData = getFilteredVisualizationData();
        const nodeConnections = filteredData.connections.filter(
          c => c.sourceProductId === nodeData.id || c.targetProductId === nodeData.id
        );
        
        // Check if any connections would result in new nodes
        return nodeConnections.some(connection => {
          const connectedProductId = connection.sourceProductId === nodeData.id 
            ? connection.targetProductId 
            : connection.sourceProductId;
          
          const connectedProduct = filteredData.products.find(p => p.id === connectedProductId);
          if (!connectedProduct) return false;
          
          // Check if the connected node is already visible
          const currentNodes = newSimulation.nodes();
          const existingNode = currentNodes.find(n => n.id === connectedProductId);
          return !existingNode; // Has expandable connections if connected node is not already visible
        });
      };

      // Add expand/collapse indicator (only for nodes with expandable connections)
      const expandIndicators = nodeEnter
        .filter(d => shouldHaveExpandIndicator(d))
        .append('circle')
        .attr('class', 'expand-indicator')
        .attr('r', 12) // Reduced from 15 to 12
        .attr('cx', 28) // Reduced from 35 to 28 for smaller nodes
        .attr('cy', -28) // Reduced from -35 to -28 for smaller nodes
        .attr('fill', d => expandedNodesSet.has(d.id) ? '#dc3545' : '#007acc')
        .attr('stroke', '#fff')
        .attr('stroke-width', 2)
        .style('cursor', 'pointer')
        .on('click', (event, d) => {
          event.stopPropagation();
          console.log(`🔵 CLICK: New node expand indicator - ${d.product.name}`);
          expandNode(d as NodeData);
        });

      // Add expand/collapse icon (only for nodes with expandable connections) - NO CLICK HANDLER to avoid double firing
      nodeEnter
        .filter(d => shouldHaveExpandIndicator(d))
        .append('text')
        .attr('class', 'expand-icon')
        .attr('x', 28) // Reduced from 35 to 28
        .attr('y', -24) // Reduced from -30 to -24
        .attr('text-anchor', 'middle')
        .attr('fill', 'white')
        .attr('font-size', '12px') // Reduced from 14px to 12px
        .attr('font-weight', 'bold')
        .text(d => expandedNodesSet.has(d.id) ? '−' : '+')
        .style('cursor', 'pointer')
        .style('user-select', 'none')
        .style('pointer-events', 'none'); // Prevent this text from receiving click events

      // Add remove indicator (X) for selected nodes (only if there are multiple nodes)
      nodeEnter
        .filter(d => d.id === selectedProduct?.id && currentNodes.length > 1)
        .append('circle')
        .attr('class', 'remove-indicator')
        .attr('r', 12) // Reduced from 15 to 12
        .attr('cx', -28) // Reduced from -35 to -28
        .attr('cy', -28) // Reduced from -35 to -28
        .attr('fill', '#dc3545')
        .attr('stroke', '#fff')
        .attr('stroke-width', 2)
        .style('cursor', 'pointer')
        .on('click', (event, d) => {
          event.stopPropagation();
          removeNodeFromVisualization(d.id);
        });

      // Add remove icon (X) for selected nodes (only if there are multiple nodes)
      nodeEnter
        .filter(d => d.id === selectedProduct?.id && currentNodes.length > 1)
        .append('text')
        .attr('class', 'remove-icon')
        .attr('x', -28) // Reduced from -35 to -28
        .attr('y', -24) // Reduced from -30 to -24
        .attr('text-anchor', 'middle')
        .attr('fill', 'white')
        .attr('font-size', '12px') // Reduced from 14px to 12px
        .attr('font-weight', 'bold')
        .text('×')
        .style('cursor', 'pointer')
        .style('user-select', 'none')
        .on('click', (event, d) => {
          event.stopPropagation();
          removeNodeFromVisualization(d.id);
        });

      // Add main product name (positioned below the circle)
      nodeEnter.append('text')
        .attr('class', 'product-name-main')
        .attr('x', 0)
        .attr('y', 60) // Reduced from 70 to 60 for smaller nodes
        .attr('text-anchor', 'middle')
        .attr('fill', svgTextColor)
        .attr('font-size', '12px')
        .attr('font-weight', 'bold')
        .each(function(d) {
          const text = d3.select(this);
          const words = d.product.name.split(' ');
          
          if (words.length === 1) {
            // Single word - truncate if too long
            const name = words[0];
            text.text(name.length > 12 ? name.substring(0, 12) + '...' : name);
          } else {
            // Multiple words - show on two lines
            text.text(''); // Clear text
            
            const firstLine = words[0];
            const secondLine = words.slice(1).join(' ');
            
            text.append('tspan')
              .attr('x', 0)
              .attr('y', 60) // Reduced from 70 to 60
              .text(firstLine.length > 12 ? firstLine.substring(0, 12) + '...' : firstLine);
            
            if (secondLine) {
              text.append('tspan')
                .attr('x', 0)
                .attr('y', 75) // Reduced from 85 to 75
                .text(secondLine.length > 12 ? secondLine.substring(0, 12) + '...' : secondLine);
            }
          }
        });

      // Add category below the product name with better positioning
      nodeEnter.append('text')
        .attr('class', 'product-category-label')
        .attr('x', 0)
        .attr('y', function(d) {
          // Calculate position based on product name length
          const words = d.product.name.split(' ');
          const hasMultipleLines = words.length > 1 && words.slice(1).join(' ').length > 0;
          return hasMultipleLines ? 95 : 80; // Reduced from 105/90 to 95/80
        })
        .attr('text-anchor', 'middle')
        .attr('fill', svgSubTextColor)
        .attr('font-size', '10px')
        .attr('font-weight', '400')
        .text(d => {
          const category = d.product.category;
          // More aggressive truncation for long categories
          return category.length > 16 ? category.substring(0, 16) + '...' : category;
        });

      // Add main click handler for node selection (not expansion)
      nodeEnter.on('click', (event, d) => {
        event.stopPropagation();
        // Prevent selection during expansion to avoid unintended selections
        if (isExpandingRef.current) {
          console.log(`🚫 Ignoring click on ${d.product.name} during expansion`);
          return;
        }
        setSelectedProduct(d.product);
        setSelectedInterface(null); // Clear interface selection when product is selected
      })
      .on('mouseenter', (event, d) => {
        // Check if node is partially out of view and gently center if needed
        const svg = d3.select(svgRef.current);
        const svgRect = svg.node()?.getBoundingClientRect();
        const nodeElement = d3.select(event.currentTarget as Element);
        const nodeRect = nodeElement.node()?.getBoundingClientRect();
        
        if (svgRect && nodeRect) {
          const padding = 150; // Padding for "View Details" button visibility
          const isNearEdge = 
            nodeRect.left < svgRect.left + padding ||
            nodeRect.right > svgRect.right - padding ||
            nodeRect.top < svgRect.top + padding ||
            nodeRect.bottom > svgRect.bottom - padding;
          
          // If node is near edges, gently center it
          if (isNearEdge && !isExpandingRef.current) {
            const focusNodeFunc = (newSimulation as any).focusNodeInViewport as ((node: NodeData, duration?: number) => void) | undefined;
            if (focusNodeFunc) {
              focusNodeFunc(d, 300); // Faster, gentler centering on hover
            }
          }
        }
      });

      // Update existing nodes' expand indicators
      const allNodes = nodes.merge(nodeEnter);
      
      // Apply dark theme filter to all node images (both new and existing)
      allNodes.select('image')
        .style('filter', isDarkTheme ? 'invert(1) brightness(0.9)' : 'none');

      // Add expand indicators to existing nodes that don't have them but should
      allNodes.each(function(d) {
        const nodeGroup = d3.select(this);
        const hasExpandable = shouldHaveExpandIndicator(d);
        const hasExpandIndicator = !nodeGroup.select('.expand-indicator').empty();
        
        // If node should have expand indicator but doesn't, add it
        if (hasExpandable && !hasExpandIndicator) {
          console.log(`➕ Adding missing expand indicator to: ${d.product.name}`);
          
          const expandIndicator = nodeGroup.append('circle')
            .attr('class', 'expand-indicator')
            .attr('r', 15)
            .attr('cx', 35)
            .attr('cy', -35)
            .attr('fill', expandedNodesSet.has(d.id) ? '#dc3545' : '#007acc')
            .attr('stroke', '#fff')
            .attr('stroke-width', 3) // Increased stroke width for better visibility
            .style('cursor', 'pointer')
            .style('opacity', 1) // Ensure full opacity
            .on('click', (event, expandedNodeData) => {
              event.stopPropagation();
              console.log(`🟡 CLICK: Existing node expand indicator - ${(expandedNodeData as NodeData).product.name}`);
              expandNode(expandedNodeData as NodeData);
            });

          const expandIcon = nodeGroup.append('text')
            .attr('class', 'expand-icon')
            .attr('x', 35)
            .attr('y', -30)
            .attr('text-anchor', 'middle')
            .attr('fill', 'white')
            .attr('font-size', '16px') // Increased font size
            .attr('font-weight', 'bold')
            .text(expandedNodesSet.has(d.id) ? '−' : '+')
            .style('cursor', 'pointer')
            .style('user-select', 'none')
            .style('opacity', 1) // Ensure full opacity
            .style('pointer-events', 'none'); // Prevent this text from receiving click events
        }
        
        // Remove expand indicators from nodes that shouldn't have them
        if (!hasExpandable && hasExpandIndicator) {
          console.log(`➖ Removing expand indicator from: ${d.product.name} (no expandable connections)`);
          nodeGroup.select('.expand-indicator').remove();
          nodeGroup.select('.expand-icon').remove();
        }
      });
      
      // Update existing expand indicators
      allNodes.select('.expand-indicator')
        .attr('fill', d => expandedNodesSet.has(d.id) ? '#dc3545' : '#007acc');
      
      allNodes.select('.expand-icon')
        .text(d => expandedNodesSet.has(d.id) ? '−' : '+');

      // Update remove indicators visibility based on selection and node count
      allNodes.select('.remove-indicator')
        .style('display', d => d.id === selectedProduct?.id && currentNodes.length > 1 ? 'block' : 'none');
      
      allNodes.select('.remove-icon')
        .style('display', d => d.id === selectedProduct?.id && currentNodes.length > 1 ? 'block' : 'none');

      // CRITICAL: Force all nodes to the front to ensure they render on top of links
      console.log(`🔍 Forcing nodes to front to ensure proper z-order`);
      
      // First, ensure the nodes group itself is after the links group
      const nodesGroupElement = nodeGroup.node();
      const linksGroupElement = linkGroup.node();
      if (nodesGroupElement && linksGroupElement && nodesGroupElement.parentNode) {
        console.log(`🔍 Moving nodes group to after links group in DOM`);
        nodesGroupElement.parentNode.appendChild(nodesGroupElement);
      }
      
      // Then move individual nodes to the front within their group  
      allNodes.each(function() {
        const parent = this.parentNode;
        if (parent) {
          parent.appendChild(this); // Move this node to the end (front) of its parent
        }
      });

      // Update positions on tick
      newSimulation.on('tick', () => {
        linkGroup.selectAll<SVGGElement, LinkData>('.link-group')
          .each(function(d) {
            const group = d3.select(this);
            const sourceNode = d.source as NodeData;
            const targetNode = d.target as NodeData;
            
            // Update line position
            group.select('.link')
              .attr('x1', sourceNode.x!)
              .attr('y1', sourceNode.y!)
              .attr('x2', targetNode.x!)
              .attr('y2', targetNode.y!);
            
            // Update interface indicator position (center of the line)
            group.select('.interface-indicator')
              .attr('transform', `translate(${(sourceNode.x! + targetNode.x!) / 2}, ${(sourceNode.y! + targetNode.y!) / 2})`);
          });

        nodeGroup.selectAll<SVGGElement, NodeData>('.node')
          .attr('transform', d => `translate(${d.x},${d.y})`);
      });
    };

    // Function to expand/collapse nodes - FIXED approach
    const expandNode = (node: NodeData) => {
      // Prevent double calls during expansion using ref for immediate check
      if (isExpandingRef.current) {
        console.log(`🚫 Expansion already in progress, ignoring call for ${node.product.name}`);
        return;
      }
      
      console.log(`=== EXPAND NODE CALLED ===`);
      console.log(`Node: ${node.product.name} (${node.id})`);
      console.log(`Call stack:`, new Error().stack);
      
      // Set lock immediately and create a unique lock ID to prevent race conditions
      const lockId = `${node.id}-${Date.now()}`;
      isExpandingRef.current = true;
      (isExpandingRef as any).currentLockId = lockId;
      
      console.log(`🔒 Expansion locked with ID: ${lockId}`);
      
      // Clear any pending timeout to prevent race conditions
      if ((isExpandingRef as any).timeoutId) {
        clearTimeout((isExpandingRef as any).timeoutId);
      }
      
      // Clear selected product if it's not the node being expanded to prevent accidental selections
      setSelectedProduct(currentSelected => {
        if (currentSelected && currentSelected.id !== node.product.id) {
          console.log(`🔄 Clearing accidental selection of ${currentSelected.name} during expansion`);
          return null;
        }
        return currentSelected;
      });
      
      // Get current state from React, not from closure
      setExpandedNodes(currentExpandedNodes => {
        console.log(`Current expanded nodes:`, Array.from(currentExpandedNodes));
        console.log(`Node is currently expanded:`, currentExpandedNodes.has(node.id));
        
        // CRITICAL: Double-check lock ID to prevent duplicate processing
        if ((isExpandingRef as any).currentLockId !== lockId) {
          console.log(`🚫 Lock ID mismatch! Expected: ${lockId}, Current: ${(isExpandingRef as any).currentLockId}`);
          console.log(`🚫 Preventing duplicate expansion processing - returning current state unchanged`);
          return currentExpandedNodes;
        }
        
        const newExpandedNodes = new Set(currentExpandedNodes);
        const currentNodes = newSimulation.nodes();
        const currentLinks = newSimulation.force<d3.ForceLink<NodeData, LinkData>>('link')?.links() || [];
        
        if (currentExpandedNodes.has(node.id)) {
        // COLLAPSE: Remove nodes that were added by this expansion
        console.log(`🔽 Collapsing node: ${node.product.name}`);
        console.log(`🔽 Before collapse - expanded nodes:`, Array.from(newExpandedNodes));
        newExpandedNodes.delete(node.id);
        console.log(`🔽 After deleting from expanded - expanded nodes:`, Array.from(newExpandedNodes));
        
        // Find nodes that have this node as their parentId (i.e., were added when this node was expanded)
        const nodesToRemove = new Set<string>();
        console.log(`🔍 Checking all nodes for parentId relationships...`);
        currentNodes.forEach(n => {
          console.log(`Node: ${n.product.name} (${n.id}) - parentId: ${n.parentId}, level: ${n.level}`);
          if (n.parentId === node.id) {
            console.log(`🗑️ Marking child node for removal: ${n.product.name} (parent: ${node.product.name})`);
            nodesToRemove.add(n.id);
          }
        });
        
        // Also remove any nodes that would become orphaned (no longer connected to any expanded nodes)
        // But keep core nodes (nodes with no parentId or level 0)
        currentNodes.forEach(n => {
          if (n.parentId && n.level > 0 && !newExpandedNodes.has(n.parentId)) {
            // Check if this node is connected to any other expanded nodes
            let hasOtherExpandedConnection = false;
            newExpandedNodes.forEach(expandedId => {
              const expandedNode = currentNodes.find(en => en.id === expandedId);
              if (expandedNode && expandedNode !== node) {
                const hasConnection = expandedNode.connections.some(conn => 
                  conn.sourceProductId === n.id || conn.targetProductId === n.id
                );
                if (hasConnection) {
                  hasOtherExpandedConnection = true;
                }
              }
            });
            
            if (!hasOtherExpandedConnection) {
              console.log(`�️ Marking orphaned node for removal: ${n.product.name}`);
              nodesToRemove.add(n.id);
            }
          }
        });
        
        console.log(`� Collapse summary: Removing ${nodesToRemove.size} nodes`);
        console.log(`Nodes being removed:`, Array.from(nodesToRemove).map(id => 
          currentNodes.find(n => n.id === id)?.product.name || id
        ));
        
        // Filter out nodes and links
        const filteredNodes = currentNodes.filter(n => !nodesToRemove.has(n.id));
        const filteredLinks = currentLinks.filter(l => 
          !nodesToRemove.has((l.source as NodeData).id) && !nodesToRemove.has((l.target as NodeData).id)
        );
        
        console.log(`Remaining nodes: ${filteredNodes.length}`);
        console.log(`Remaining links: ${filteredLinks.length}`);
        
        // Update state with filtered data
        setNodes(filteredNodes);
        setLinks(filteredLinks);
        
      } else {
        // EXPAND: Add connected nodes that aren't already visible
        console.log(`🔼 Expanding node: ${node.product.name}`);
        newExpandedNodes.add(node.id);
        
        // Schedule centering after expansion animation completes
        const focusNodeFunc = (newSimulation as any).focusNodeInViewport as ((node: NodeData, duration?: number) => void) | undefined;
        if (focusNodeFunc) {
          setTimeout(() => {
            focusNodeFunc(node, 500);
          }, 200); // Delay to allow expansion animation to start
        }
        
        const newNodes: NodeData[] = [];
        const newLinks: LinkData[] = [];
        
        // Get connections for this node from filtered data
        const expansionFilteredData = getFilteredVisualizationData();
        const nodeConnections = expansionFilteredData.connections.filter(
          c => c.sourceProductId === node.id || c.targetProductId === node.id
        );
        
        console.log(`📊 Found ${nodeConnections.length} connections for ${node.product.name}`);
        console.log(`Available products in filtered data: ${expansionFilteredData.products.length}`);
        console.log(`Connections:`, nodeConnections.map(c => `${c.sourceProductId} -> ${c.targetProductId}`));
        
        // Use a Set to track connected product IDs to avoid duplicates
        const connectedProductIds = new Set<string>();
        
        // First pass: collect all unique connected product IDs
        nodeConnections.forEach((connection) => {
          const connectedProductId = connection.sourceProductId === node.id 
            ? connection.targetProductId 
            : connection.sourceProductId;
          
          if (!connectedProductIds.has(connectedProductId)) {
            const connectedProduct = expansionFilteredData.products.find(p => p.id === connectedProductId);
            if (connectedProduct) {
              connectedProductIds.add(connectedProductId);
            }
          }
        });
        
        console.log(`📊 Total unique connections to process: ${connectedProductIds.size}`);
        
        let nodeIndex = 0; // Track actual node creation index for proper angle distribution
        
        // Second pass: process each unique connection
        nodeConnections.forEach((connection) => {
          const connectedProductId = connection.sourceProductId === node.id 
            ? connection.targetProductId 
            : connection.sourceProductId;
          
          // Skip if we've already processed this product (avoid duplicates)
          if (!connectedProductIds.has(connectedProductId)) {
            return;
          }
          
          // Remove from set so we don't process it again
          connectedProductIds.delete(connectedProductId);
          
          const connectedProduct = expansionFilteredData.products.find(p => p.id === connectedProductId);
          if (!connectedProduct) {
            console.log(`⏭️ Product not in filtered data: ${connectedProductId}`);
            return;
          }
          
          // Check if node already exists
          const existingNode = currentNodes.find(n => n.id === connectedProductId);
          if (existingNode) {
            console.log(`🔗 Node already exists: ${connectedProduct.name}, ensuring link...`);
            
            // NEVER set parentId for existing root nodes (level 0) to prevent incorrect removal during collapse
            // Only set parentId for nodes that are already child nodes and don't have a parentId yet
            if (!existingNode.parentId && existingNode.level > 0) {
              existingNode.parentId = node.id;
              console.log(`📌 Setting parentId for existing child node: ${connectedProduct.name} -> parent: ${node.product.name}`);
            } else if (existingNode.level === 0) {
              console.log(`📌 Preserving root node status for ${connectedProduct.name} (level 0) - NOT setting parentId`);
            } else {
              console.log(`📌 Existing node ${connectedProduct.name} already has parentId: ${existingNode.parentId}, keeping existing relationship`);
            }
            
            // Ensure link exists
            const linkExists = currentLinks.some(l => 
              ((l.source as NodeData).id === node.id && (l.target as NodeData).id === connectedProductId) ||
              ((l.source as NodeData).id === connectedProductId && (l.target as NodeData).id === node.id)
            );
            
            if (!linkExists) {
              console.log(`➕ Adding link: ${node.product.name} <-> ${connectedProduct.name}`);
              newLinks.push({
                connection,
                source: node,
                target: existingNode
              });
            } else {
              console.log(`⏭️ Link already exists: ${node.product.name} <-> ${connectedProduct.name}`);
            }
            
            nodeIndex++; // Increment for existing nodes too to maintain angle distribution
          } else {
            console.log(`🆕 Adding new node: ${connectedProduct.name}`);
            
            // Position new node around the expanded node using proper 360-degree distribution
            const totalUniqueConnections = Array.from(new Set(
              nodeConnections.map(c => c.sourceProductId === node.id ? c.targetProductId : c.sourceProductId)
            )).filter(id => expansionFilteredData.products.find(p => p.id === id)).length;
            
            const angle = (2 * Math.PI * nodeIndex) / totalUniqueConnections; // Distribute across full circle
            
            // Simple fixed distance - let users zoom/pan to see nodes outside canvas
            const distance = 250; // Fixed consistent distance
            
            let newX = node.x! + Math.cos(angle) * distance;
            let newY = node.y! + Math.sin(angle) * distance;
            
            // Check for collisions with existing nodes and adjust position if needed
            const collisionRadius = 120; // Minimum distance between node centers (larger than visual radius)
            let attempts = 0;
            const maxAttempts = 8; // Try up to 8 different positions
            
            while (attempts < maxAttempts) {
              let hasCollision = false;
              
              // Check collision with all existing nodes
              for (const existingNode of currentNodes) {
                const dx = newX - existingNode.x!;
                const dy = newY - existingNode.y!;
                const distance = Math.sqrt(dx * dx + dy * dy);
                
                if (distance < collisionRadius) {
                  hasCollision = true;
                  break;
                }
              }
              
              if (!hasCollision) {
                console.log(`📍 Collision-free position found for ${connectedProduct.name} at attempt ${attempts + 1}`);
                break;
              }
              
              // Try a different angle with slight random offset
              const adjustedAngle = angle + (attempts * Math.PI / 4) + (Math.random() - 0.5) * 0.5;
              const adjustedDistance = distance + (attempts * 30); // Gradually increase distance
              newX = node.x! + Math.cos(adjustedAngle) * adjustedDistance;
              newY = node.y! + Math.sin(adjustedAngle) * adjustedDistance;
              
              attempts++;
              console.log(`⚠️ Collision detected for ${connectedProduct.name}, trying adjusted position (attempt ${attempts})`);
            }
            
            if (attempts >= maxAttempts) {
              console.log(`⚠️ Could not find collision-free position for ${connectedProduct.name}, using last attempt`);
            }
            
            console.log(`📍 Positioning node ${nodeIndex}/${totalUniqueConnections} at angle ${(angle * 180 / Math.PI).toFixed(1)}° - (${newX.toFixed(1)}, ${newY.toFixed(1)}) [Distance: ${Math.sqrt((newX - node.x!) ** 2 + (newY - node.y!) ** 2).toFixed(0)}px] [Attempts: ${attempts + 1}]`);
            
            const newNode: NodeData = {
              id: connectedProductId,
              product: connectedProduct,
              expanded: false,
              connections: getConnectionsForProduct(connectedProductId),
              level: node.level + 1,
              parentId: node.id, // This is key for collapse functionality
              x: newX,
              y: newY,
              fx: newX, // Keep permanently fixed at calculated position
              fy: newY, // Keep permanently fixed at calculated position
              vx: 0,
              vy: 0
            };
            
            newNodes.push(newNode);
            nodeIndex++; // Increment only when actually creating a new node
            
            // Don't release fixed positions - keep nodes exactly where we positioned them
            // Users can drag nodes manually if they want to reposition them
            
            newLinks.push({
              connection,
              source: node,
              target: newNode
            });
          }
        });
        
        console.log(`📊 Expand summary: Adding ${newNodes.length} new nodes, ${newLinks.length} new links`);
        if (newNodes.length > 0) {
          console.log(`New nodes:`, newNodes.map(n => n.product.name));
        }
        
        // Early return if no new content to prevent unnecessary state updates
        if (newNodes.length === 0 && newLinks.length === 0) {
          console.log(`📊 No new nodes or links to add - returning early to prevent duplicate state updates`);
          return newExpandedNodes; // Just update the expanded state and exit
        }
        
        // Keep all existing nodes completely fixed during expansion to prevent motion
        if (newNodes.length > 0) {
          console.log(`🔒 Permanently fixing positions of all existing nodes during expansion`);
          currentNodes.forEach(existingNode => {
            if (!existingNode.fx && !existingNode.fy) {
              existingNode.fx = existingNode.x;
              existingNode.fy = existingNode.y;
            }
          });
          
          // Don't release fixed positions automatically - users can drag to reposition
          console.log(`📌 All existing nodes will remain fixed at current positions`);
        }
        
        // Update state (avoid duplicates)
        setNodes(prev => {
          const existingIds = new Set(prev.map(n => n.id));
          const nodesToAdd = newNodes.filter(n => !existingIds.has(n.id));
          console.log(`📊 Adding ${nodesToAdd.length} new nodes to state (filtered duplicates)`);
          
          const updatedNodes = [...prev, ...nodesToAdd];
          
          // Don't release the center node's position during expansion - keep it fixed
          // The center node should stay in place when child nodes are added around it
          // Position will only be released when user manually drags the node
          console.log(`📌 Keeping center node fixed in place during expansion`);
          
          // Update simulation immediately with new nodes
          if (newSimulation && nodesToAdd.length > 0) {
            newSimulation.nodes(updatedNodes);
            
            // Keep minimal forces active for rendering but prevent major movement
            newSimulation
              .force('charge', d3.forceManyBody().strength(-1200)) // Increased repulsion for better collision avoidance
              .force('collision', d3.forceCollide().radius(120)) // Increased collision radius for better spacing
              .alpha(0.1) // Higher energy to allow collision avoidance movement
              .restart();
            
            console.log(`� Minimal simulation forces enabled for rendering`);
          }
          
          return updatedNodes;
        });
        setLinks(prev => {
          const existingLinks = new Set(prev.map(l => `${(l.source as NodeData).id}-${(l.target as NodeData).id}`));
          const linksToAdd = newLinks.filter(l => !existingLinks.has(`${(l.source as NodeData).id}-${(l.target as NodeData).id}`));
          console.log(`📊 Adding ${linksToAdd.length} new links to state (filtered duplicates)`);
          
          const updatedLinks = [...prev, ...linksToAdd];
          
          // Update simulation links immediately
          if (newSimulation && linksToAdd.length > 0) {
            newSimulation.force<d3.ForceLink<NodeData, LinkData>>('link')?.links(updatedLinks);
            
            // Configure link force with weak strength to prevent major repositioning
            newSimulation.force('link', d3.forceLink<NodeData, LinkData>(updatedLinks)
              .id(d => d.id)
              .distance(250) // Match our fixed positioning distance
              .strength(0.05) // Even weaker link strength to minimize movement while allowing collision avoidance
            );
            
            console.log(`🔗 Weak link forces enabled for connector rendering`);
          }
          
          return updatedLinks;
        });
      }
      
      // Update expanded state
      setExpandedNodes(newExpandedNodes);
      console.log(`🔄 Updated expanded nodes:`, Array.from(newExpandedNodes));
      
      // Don't call updateVisualization here - let the useEffect handle it
      // This prevents double rendering and state conflicts
      console.log(`📊 Skipping immediate updateVisualization - letting useEffect handle it`);
      
      console.log(`=== EXPAND NODE COMPLETED ===`);
      
      // Unlock expansion after a longer delay to ensure state updates are fully complete
      (isExpandingRef as any).timeoutId = setTimeout(() => {
        // Only unlock if this is still the current lock
        if ((isExpandingRef as any).currentLockId === lockId) {
          isExpandingRef.current = false;
          console.log(`🔓 Expansion lock released for ${node.product.name} (ID: ${lockId})`);
        } else {
          console.log(`🚫 Lock ID mismatch, not releasing lock for ${node.product.name}`);
        }
      }, 1000); // Increased delay to prevent double expansion calls
      
      return newExpandedNodes; // Return the new state
    });
  };

    // Store updateVisualization function reference
    (newSimulation as any).updateVisualization = updateVisualization;

    updateVisualization();

    return () => {
      newSimulation.stop();
    };
  }, []); // IMPORTANT: Empty dependency array to prevent re-initialization when filters change

  // Handle window resize to update SVG dimensions
  useEffect(() => {
    const handleResize = () => {
      if (!svgRef.current || !simulation) return;
      
      const container = svgRef.current.parentElement;
      const containerRect = container?.getBoundingClientRect();
      const newWidth = containerRect?.width || 1200;
      const newHeight = Math.max(600, containerRect?.height || 800);
      
      // Update SVG dimensions
      const svg = d3.select(svgRef.current);
      svg.attr('width', newWidth)
         .attr('height', newHeight)
         .attr('viewBox', `0 0 ${newWidth} ${newHeight}`);
      
      // Update stored dimensions in simulation
      (simulation as any).width = newWidth;
      (simulation as any).height = newHeight;
      
      // Update force center
      simulation.force('center', d3.forceCenter(newWidth / 2, newHeight / 2));
      simulation.force('x', d3.forceX(newWidth / 2).strength(0.02));
      simulation.force('y', d3.forceY(newHeight / 2).strength(0.02));
      
      console.log(`📏 Canvas resized to: ${newWidth}x${newHeight}`);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [simulation]);

  // Handle bottom panel visibility changes to recalculate canvas size
  useEffect(() => {
    if (!svgRef.current || !simulation) return;
    
    // Small delay to allow DOM to update after panel visibility change
    setTimeout(() => {
      const container = svgRef.current!.parentElement;
      const containerRect = container?.getBoundingClientRect();
      const newWidth = containerRect?.width || 1200;
      const newHeight = Math.max(600, containerRect?.height || 800);
      
      // Only update if dimensions actually changed
      const currentWidth = (simulation as any).width || 1200;
      const currentHeight = (simulation as any).height || 800;
      
      if (Math.abs(newWidth - currentWidth) > 5 || Math.abs(newHeight - currentHeight) > 5) {
        console.log(`📏 Canvas resized due to bottom panel change: ${currentWidth}x${currentHeight} → ${newWidth}x${newHeight}`);
        
        // Calculate scaling factors
        const scaleX = newWidth / currentWidth;
        const scaleY = newHeight / currentHeight;
        
        // Get current nodes and preserve their relative positions
        const currentNodes = simulation.nodes();
        
        // Store the old center for reference
        const oldCenterX = currentWidth / 2;
        const oldCenterY = currentHeight / 2;
        const newCenterX = newWidth / 2;
        const newCenterY = newHeight / 2;
        
        console.log(`📏 Rescaling nodes: scaleX=${scaleX.toFixed(3)}, scaleY=${scaleY.toFixed(3)}`);
        console.log(`📏 Center shift: (${oldCenterX}, ${oldCenterY}) → (${newCenterX}, ${newCenterY})`);
        
        // Update all node positions proportionally
        currentNodes.forEach((node, index) => {
          if (node.x !== undefined && node.y !== undefined) {
            // Calculate distance from old center
            const oldDistanceX = node.x - oldCenterX;
            const oldDistanceY = node.y - oldCenterY;
            
            // Maintain the same relative distance from new center
            const newX = newCenterX + (oldDistanceX * scaleX);
            const newY = newCenterY + (oldDistanceY * scaleY);
            
            console.log(`📏 Node ${index} (${node.product.name.substring(0, 10)}): (${node.x.toFixed(1)}, ${node.y.toFixed(1)}) → (${newX.toFixed(1)}, ${newY.toFixed(1)})`);
            
            // Update node position
            node.x = newX;
            node.y = newY;
            
            // If node is fixed, update fixed positions too
            if (node.fx !== null && node.fx !== undefined) {
              node.fx = newX;
            }
            if (node.fy !== null && node.fy !== undefined) {
              node.fy = newY;
            }
          }
        });
        
        // Update SVG dimensions
        const svg = d3.select(svgRef.current);
        svg.attr('width', newWidth)
           .attr('height', newHeight)
           .attr('viewBox', `0 0 ${newWidth} ${newHeight}`);
        
        // Update stored dimensions in simulation
        (simulation as any).width = newWidth;
        (simulation as any).height = newHeight;
        
        // Update force center to new dimensions
        simulation.force('center', d3.forceCenter(newWidth / 2, newHeight / 2));
        simulation.force('x', d3.forceX(newWidth / 2).strength(0.02));
        simulation.force('y', d3.forceY(newHeight / 2).strength(0.02));
        
        // Update all DOM elements to match new positions immediately
        svg.selectAll<SVGGElement, NodeData>('.node')
          .attr('transform', (d: NodeData) => `translate(${d.x},${d.y})`);
        
        svg.selectAll<SVGLineElement, LinkData>('.link-group line')
          .attr('x1', (d: LinkData) => (d.source as NodeData).x!)
          .attr('y1', (d: LinkData) => (d.source as NodeData).y!)
          .attr('x2', (d: LinkData) => (d.target as NodeData).x!)
          .attr('y2', (d: LinkData) => (d.target as NodeData).y!);
        
        // Update interface icons positions
        svg.selectAll<SVGGElement, LinkData>('.interface-icon')
          .attr('transform', (d: LinkData) => {
            const sourceX = (d.source as NodeData).x!;
            const sourceY = (d.source as NodeData).y!;
            const targetX = (d.target as NodeData).x!;
            const targetY = (d.target as NodeData).y!;
            const midX = (sourceX + targetX) / 2;
            const midY = (sourceY + targetY) / 2;
            return `translate(${midX},${midY})`;
          });
        
        // Don't restart simulation forces - just maintain current positions
        console.log(`📏 Canvas resize complete - ${currentNodes.length} nodes repositioned proportionally`);
      }
    }, 10);
  }, [selectedProduct, selectedInterface, simulation]);

  // Update simulation when nodes or links change
  useEffect(() => {
    if (!simulation) return;
    
    console.log(`🔄 Updating simulation with ${nodes.length} nodes and ${links.length} links`);
    console.log(`🔄 Node names:`, nodes.map(n => n.product.name));
    
    // Check if this is the first node being added directly from sidebar click (avoid double update)
    const simWidth = (simulation as any).width || 1200;
    const simHeight = (simulation as any).height || 800;
    const centerX = simWidth / 2;
    const centerY = simHeight / 2;
    
    if (nodes.length === 1 && links.length === 0 && 
        Math.abs(nodes[0].fx! - centerX) < 1 && Math.abs(nodes[0].fy! - centerY) < 1) {
      console.log(`⏭️ Skipping useEffect update for first node clicked from sidebar (already handled directly)`);
      return;
    }
    
    // Update simulation data
    simulation.nodes(nodes);
    simulation.force<d3.ForceLink<NodeData, LinkData>>('link')?.links(links);
    
    // Ensure minimal forces are always active for rendering and collision detection
    simulation
      .force('charge', d3.forceManyBody().strength(-1200)) // Increased repulsion for better collision avoidance
      .force('collision', d3.forceCollide().radius(120)) // Increased collision radius for better spacing
      .force('link', d3.forceLink<NodeData, LinkData>(links)
        .id(d => d.id)
        .distance(250) // Match our expansion distance
        .strength(0.05) // Even weaker link strength to minimize movement while allowing collision avoidance
      );
    
    // Call the stored updateVisualization function
    const updateVisualizationFunc = (simulation as any).updateVisualization;
    if (updateVisualizationFunc) {
      console.log(`📊 Calling stored updateVisualization function...`);
      updateVisualizationFunc(expandedNodes);
    }
    
    // Only restart simulation if there are changes that need repositioning
    if (nodes.length > 0) {
      // Very gentle restart to avoid jumping
      simulation.alpha(0.05).restart();
    }
  }, [nodes, links, simulation, expandedNodes]);

  // Reset expanded nodes when filters change
  useEffect(() => {
    // Only reset if we're actually changing filters, not on initial load
    const hasFilters = selectedCategory !== 'all' || selectedTeam !== 'all' || selectedType !== 'all' || searchTerm !== '';
    if (hasFilters) {
      setExpandedNodes(new Set());
    }
  }, [selectedCategory, selectedTeam, searchTerm, selectedType]);

  const addProductToVisualization = (product: Product, dropPosition?: { x: number; y: number }, autoSelect: boolean = true) => {
    console.log(`🎯 Adding product to visualization: ${product.name} (${product.id})`);
    
    if (!simulation) {
      console.log(`❌ No simulation available`);
      return;
    }

    const currentNodes = simulation.nodes();
    const existingNode = currentNodes.find(n => n.id === product.id);
    
    if (existingNode) {
      console.log(`✅ Product already in visualization, selecting: ${product.name}`);
      if (autoSelect) {
        setSelectedProduct(product);
        setSelectedInterface(null); // Clear interface selection when product is selected
      }
      return;
    }

    // Calculate position for new node
    let newX, newY;
    const simWidth = (simulation as any).width || 1200;
    const simHeight = (simulation as any).height || 800;
    
    if (dropPosition) {
      // Use drop position if provided (for drag and drop)
      newX = dropPosition.x;
      newY = dropPosition.y;
      console.log(`📍 Drag and drop - positioning at (${newX}, ${newY})`);
    } else if (currentNodes.length === 0) {
      // First node - center it
      newX = simWidth / 2;
      newY = simHeight / 2;
      console.log(`📍 First node - centering at (${newX}, ${newY})`);
    } else {
      // Subsequent nodes - position around existing nodes
      newX = simWidth / 2 + (Math.random() - 0.5) * (simWidth * 0.33);
      newY = simHeight / 2 + (Math.random() - 0.5) * (simHeight * 0.375);
      console.log(`📍 Additional node - positioning at (${newX}, ${newY})`);
    }

    // If this is the first node (from drag & drop), use direct DOM approach like click
    if (currentNodes.length === 0) {
      console.log(`🎯 First node via drag & drop - using direct DOM approach`);
      
      const newNode: NodeData = {
        id: product.id,
        product,
        expanded: false,
        connections: getConnectionsForProduct(product.id),
        level: 0,
        parentId: undefined,
        x: newX,
        y: newY,
        fx: newX,
        fy: newY,
        vx: 0,
        vy: 0
      };

      if (autoSelect) {
        setSelectedProduct(product);
        setSelectedInterface(null); // Clear interface selection when product is selected
      }
      
      // Use the same direct DOM approach as click
      if (simulation && svgRef.current) {
        // Stop simulation completely to prevent any tick events
        simulation.stop();
        simulation.alpha(0);
        simulation.alphaTarget(0);
        
        // Clear any existing simulation forces and nodes
        simulation.nodes([]);
        simulation.force('charge', null);
        simulation.force('center', null);
        simulation.force('x', null);
        simulation.force('y', null);
        simulation.force('collision', null);
        simulation.force('link', d3.forceLink([]));
        
        // Get SVG and node group
        const svg = d3.select(svgRef.current);
        const nodeGroup = svg.select('.nodes');
        
        // Clear any existing nodes
        nodeGroup.selectAll('.node').remove();
        
        // Create the node element directly at drop position
        const nodeEnter = nodeGroup.append('g')
          .datum(newNode)
          .attr('class', 'node first-node')
          .attr('transform', `translate(${newX},${newY})`) // Immediate positioning
          .style('opacity', 1); // Ensure it's visible immediately
        
        // Add circle with gradient fill
        nodeEnter.append('circle')
          .attr('r', 40) // Reduced from 50 to 40
          .attr('fill', newNode.product.type === 'internal' ? 'url(#internal-gradient)' : 'url(#external-gradient)')
          .attr('stroke', newNode.product.type === 'internal' ? '#28a745' : '#ffc107')
          .attr('stroke-width', 3) // Reduced from 4 to 3
          .style('cursor', 'pointer');

        // Add product icon inside the circle
        nodeEnter.append('image')
          .attr('href', `/icons/${getProductIcon(newNode.product)}`)
          .attr('x', -25) // Center the 50px icon (reduced from 60px)
          .attr('y', -25)
          .attr('width', 50) // Reduced from 60 to 50
          .attr('height', 50) // Reduced from 60 to 50
          .style('cursor', 'pointer')
          .style('pointer-events', 'none')
          .on('error', function() {
            console.error(`❌ Failed to load icon for ${newNode.product.name}: /icons/${getProductIcon(newNode.product)}`);
          })
          .on('load', function() {
            console.log(`✅ Successfully loaded icon for ${newNode.product.name}`);
          });
        
        // Add main product name text (positioned below the circle)
        nodeEnter.append('text')
          .attr('class', 'product-name-main')
          .attr('x', 0)
          .attr('y', 60) // Reduced from 70 to 60
          .attr('text-anchor', 'middle')
          .attr('fill', svgTextColor)
          .attr('font-size', '12px')
          .attr('font-weight', 'bold')
          .each(function(d) {
            const text = d3.select(this);
            const words = d.product.name.split(' ');
            
            if (words.length === 1) {
              const name = words[0];
              text.text(name.length > 12 ? name.substring(0, 12) + '...' : name);
            } else {
              text.text('');
              const firstLine = words[0];
              const secondLine = words.slice(1).join(' ');
              
              text.append('tspan')
                .attr('x', 0)
                .attr('y', 60) // Reduced from 70 to 60
                .text(firstLine.length > 12 ? firstLine.substring(0, 12) + '...' : firstLine);
              
              if (secondLine) {
                text.append('tspan')
                  .attr('x', 0)
                  .attr('y', 75) // Reduced from 85 to 75
                  .text(secondLine.length > 12 ? secondLine.substring(0, 12) + '...' : secondLine);
              }
            }
          });
        
        // Add category label (positioned below the product name)
        nodeEnter.append('text')
          .attr('class', 'product-category-label')
          .attr('x', 0)
          .attr('y', function(d) {
            // Calculate position based on product name length
            const words = d.product.name.split(' ');
            const hasMultipleLines = words.length > 1 && words.slice(1).join(' ').length > 0;
            return hasMultipleLines ? 95 : 80; // Reduced from 105/90 to 95/80
          })
          .attr('text-anchor', 'middle')
          .attr('fill', svgSubTextColor)
          .attr('font-size', '10px')
          .attr('font-weight', '400')
          .text(d => {
            const category = d.product.category;
            // More aggressive truncation for long categories
            return category.length > 16 ? category.substring(0, 16) + '...' : category;
          });
        
        // Add click handler for selection
        nodeEnter.on('click', (event, d) => {
          event.stopPropagation();
          setSelectedProduct(d.product);
        });
        
        // Add drag behavior
        nodeEnter.call(d3.drag<SVGGElement, NodeData>()
          .on('start', (event, d) => {
            d.fx = d.x;
            d.fy = d.y;
          })
          .on('drag', (event, d) => {
            d.fx = event.x;
            d.fy = event.y;
            d.x = event.x;
            d.y = event.y;
            d3.select(event.sourceEvent.target.parentNode)
              .attr('transform', `translate(${d.x},${d.y})`);
          })
          .on('end', (event, d) => {
            d.fx = null;
            d.fy = null;
          })
        );
        
        console.log(`🎨 First node DOM element created directly at drop position without simulation`);
        
        // Now update simulation with the node for future interactions
        simulation.nodes([newNode]);
        
        // Update React state last
        setNodes([newNode]);
        setLinks([]);
        
        // Trigger updateVisualization to add expand indicators and other interactive elements
        setTimeout(() => {
          const updateVisualizationFunc = (simulation as any).updateVisualization;
          if (updateVisualizationFunc) {
            console.log(`🔄 Calling updateVisualization to add expand indicators`);
            updateVisualizationFunc(new Set());
          }
        }, 0);
        
        console.log(`✅ First node via drag & drop rendered immediately without any animation`);
        return;
      }
    }

    // For subsequent nodes, use the regular approach
    const newNode: NodeData = {
      id: product.id,
      product,
      expanded: false,
      connections: getConnectionsForProduct(product.id),
      level: 0,
      parentId: undefined, // Manually added nodes have no parent
      x: newX,
      y: newY,
      fx: newX, // Fix initial position to prevent jumping
      fy: newY, // Fix initial position to prevent jumping
      vx: 0,    // Set initial velocity to 0
      vy: 0     // Set initial velocity to 0
    };

    console.log(`🆕 Creating new node:`, newNode);
    console.log(`🔗 Node has ${newNode.connections.length} connections`);

    // Single state update to prevent double rendering
    setNodes(prev => {
      const existingIds = new Set(prev.map(n => n.id));
      if (!existingIds.has(newNode.id)) {
        console.log(`📊 Adding node to React state: ${newNode.product.name}`);
        const updatedNodes = [...prev, newNode];
        
        // Update the simulation immediately with the new node to prevent blink
        if (simulation) {
          simulation.nodes(updatedNodes);
          simulation.tick(); // Single tick for immediate positioning
        }
        
        // Set selected product
        if (autoSelect) {
          setSelectedProduct(product);
          setSelectedInterface(null); // Clear interface selection when product is selected
        }
        
        // Release fixed position after a delay
        setTimeout(() => {
          newNode.fx = null;
          newNode.fy = null;
          if (simulation) {
            simulation.alpha(0.005).restart(); // Very gentle restart
          }
        }, 300); // Reduced delay for quicker settling
        
        return updatedNodes;
      }
      console.log(`⚠️ Node ${newNode.product.name} already exists in React state, skipping`);
      return prev;
    });
    
    console.log(`✅ Product added successfully: ${product.name}`);
  };

  const clearVisualization = () => {
    // Reset all state
    setNodes([]);
    setLinks([]);
    setSelectedProduct(null);
    setSelectedInterface(null); // Clear selected interface as well
    setExpandedNodes(new Set());
    
    // Clear the simulation
    if (simulation) {
      simulation.nodes([]);
      simulation.force<d3.ForceLink<NodeData, LinkData>>('link')?.links([]);
      
      // Clear the SVG
      if (svgRef.current) {
        const svg = d3.select(svgRef.current);
        svg.selectAll('.node').remove();
        svg.selectAll('.link').remove();
        svg.selectAll('.interface-indicator').remove();
      }
      
      simulation.alpha(0).restart();
    }
    
    console.log('🧹 Visualization cleared');
  };

  return (
    <div className="integration-visualization">
      <div className="visualization-header">
        <h1>Product Integration Ecosystem</h1>
        <div className="visualization-controls">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-filter"
          >
            {categories.map(category => (
              <option key={category} value={category}>
                {category === 'all' ? 'All Categories' : category}
              </option>
            ))}
          </select>
          <select
            value={selectedTeam}
            onChange={(e) => setSelectedTeam(e.target.value)}
            className="team-filter"
          >
            {teams.map(team => (
              <option key={team} value={team}>
                {team === 'all' ? 'All Teams' : team}
              </option>
            ))}
          </select>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="type-filter"
          >
            {types.map(type => (
              <option key={type} value={type}>
                {type === 'all' ? 'All Types' : type === 'internal' ? 'Internal (MSI)' : 'External'}
              </option>
            ))}
          </select>
          <button
            onClick={clearVisualization}
            className="clear-button"
            title="Clear all nodes and reset visualization"
          >
            Clear
          </button>
          <div className="legend">
            <div className="legend-section">
              <span className="legend-title">Products:</span>
              <div className="legend-item">
                <div className="legend-color internal"></div>
                <span>Internal (MSI)</span>
              </div>
              <div className="legend-item">
                <div className="legend-color external"></div>
                <span>External</span>
              </div>
            </div>
            <div className="legend-section">
              <span className="legend-title">Interfaces:</span>
              <div className="legend-item">
                <div className="legend-color interface-active"></div>
                <span>Active</span>
              </div>
              <div className="legend-item">
                <div className="legend-color interface-planned"></div>
                <span>Planned</span>
              </div>
              <div className="legend-item">
                <div className="legend-color interface-deprecated"></div>
                <span>Deprecated</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="visualization-content">
        <div className="left-sidebar">
          {/* Products Panel */}
          <div className={`sidebar-panel products-panel ${showProductsPanel ? 'expanded' : 'collapsed'}`}>
            <div className="panel-header" onClick={() => setShowProductsPanel(!showProductsPanel)}>
              {showProductsPanel ? (
                <>
                  <h3>Products</h3>
                  <span className="panel-toggle">{"<<"}</span>
                </>
              ) : (
                <>
                  <h3 className="vertical-title">Products</h3>
                  <span className="panel-toggle">{"+"}</span>
                </>
              )}
            </div>
            {showProductsPanel && (
              <div className="product-controls">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="product-search-input"
                />
                <div className="product-list">
                  {filteredProducts.map(product => (
              <div
                key={product.id}
                className={`product-item ${product.type} ${selectedProduct?.id === product.id ? 'selected' : ''}`}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('application/json', JSON.stringify(product));
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                onClick={() => {
                  // If this is the first product, add it directly without any animation
                  if (nodes.length === 0) {
                    console.log(`🎯 Adding FIRST product directly: ${product.name}`);
                    
                    // Use the actual canvas center coordinates
                    const simWidth = (simulation as any)?.width || 1200;
                    const simHeight = (simulation as any)?.height || 800;
                    const centerX = simWidth / 2;
                    const centerY = simHeight / 2;
                    
                    const newNode: NodeData = {
                      id: product.id,
                      product,
                      expanded: false,
                      connections: getConnectionsForProduct(product.id),
                      level: 0,
                      parentId: undefined, // Manually added nodes have no parent
                      x: centerX,
                      y: centerY,
                      fx: centerX, // Fix it in center
                      fy: centerY,
                      vx: 0,    // Set initial velocity to 0
                      vy: 0     // Set initial velocity to 0
                    };
                    
                    // Set state directly without triggering useEffect chain
                    console.log(`📌 Setting state directly for first node at center (${centerX}, ${centerY})`);
                    setSelectedProduct(product);
                    setSelectedInterface(null); // Clear interface selection when product is selected
                    
                    // Update simulation FIRST before setting React state
                    if (simulation) {
                      // Stop simulation completely to prevent any tick events
                      simulation.stop();
                      simulation.alpha(0);
                      simulation.alphaTarget(0);
                      
                      // Clear any existing simulation forces and nodes
                      simulation.nodes([]);
                      simulation.force('charge', null);
                      simulation.force('center', null);
                      simulation.force('x', null);
                      simulation.force('y', null);
                      simulation.force('collision', null);
                      simulation.force('link', d3.forceLink([]));
                      
                      // Get SVG and node group
                      const svg = d3.select(svgRef.current);
                      const nodeGroup = svg.select('.nodes');
                      
                      // Clear any existing nodes
                      nodeGroup.selectAll('.node').remove();
                      
                      // Create the node element directly at center position
                      const nodeEnter = nodeGroup.append('g')
                        .datum(newNode)
                        .attr('class', 'node first-node')
                        .attr('transform', `translate(${centerX},${centerY})`) // Immediate positioning
                        .style('opacity', 1); // Ensure it's visible immediately
                      
                      // Add circle with gradient fill
                      nodeEnter.append('circle')
                        .attr('r', 40) // Reduced from 50 to 40
                        .attr('fill', newNode.product.type === 'internal' ? 'url(#internal-gradient)' : 'url(#external-gradient)')
                        .attr('stroke', newNode.product.type === 'internal' ? '#28a745' : '#ffc107')
                        .attr('stroke-width', 3) // Reduced from 4 to 3
                        .style('cursor', 'pointer');

                      // Add product icon inside the circle
                      nodeEnter.append('image')
                        .attr('href', `/icons/${getProductIcon(newNode.product)}`)
                        .attr('x', -25) // Center the 50px icon (reduced from 60px)
                        .attr('y', -25)
                        .attr('width', 50) // Reduced from 60 to 50
                        .attr('height', 50) // Reduced from 60 to 50
                        .style('cursor', 'pointer')
                        .style('pointer-events', 'none')
                        .on('error', function() {
                          console.error(`❌ Failed to load icon for ${newNode.product.name}: /icons/${getProductIcon(newNode.product)}`);
                        })
                        .on('load', function() {
                          console.log(`✅ Successfully loaded icon for ${newNode.product.name}`);
                        });
                      
                      // Add main product name text (positioned below the circle)
                      nodeEnter.append('text')
                        .attr('class', 'product-name-main')
                        .attr('x', 0)
                        .attr('y', 60) // Reduced from 70 to 60
                        .attr('text-anchor', 'middle')
                        .attr('fill', svgTextColor)
                        .attr('font-size', '12px')
                        .attr('font-weight', 'bold')
                        .each(function(d) {
                          const text = d3.select(this);
                          const words = d.product.name.split(' ');
                          
                          if (words.length === 1) {
                            const name = words[0];
                            text.text(name.length > 12 ? name.substring(0, 12) + '...' : name);
                          } else {
                            text.text(''); // Clear text
                            const firstLine = words[0];
                            const secondLine = words.slice(1).join(' ');
                            
                            text.append('tspan')
                              .attr('x', 0)
                              .attr('y', 60) // Reduced from 70 to 60
                              .text(firstLine.length > 12 ? firstLine.substring(0, 12) + '...' : firstLine);
                            
                            if (secondLine) {
                              text.append('tspan')
                                .attr('x', 0)
                                .attr('y', 75) // Reduced from 85 to 75
                                .text(secondLine.length > 12 ? secondLine.substring(0, 12) + '...' : secondLine);
                            }
                          }
                        });
                      
                      // Add category label
                      nodeEnter.append('text')
                        .attr('class', 'product-category-label')
                        .attr('x', 0)
                        .attr('y', function(d) {
                          // Calculate position based on product name length
                          const words = d.product.name.split(' ');
                          const hasMultipleLines = words.length > 1 && words.slice(1).join(' ').length > 0;
                          return hasMultipleLines ? 95 : 80; // Reduced from 105/90 to 95/80
                        })
                        .attr('text-anchor', 'middle')
                        .attr('fill', svgSubTextColor)
                        .attr('font-size', '10px')
                        .attr('font-weight', '400')
                        .text(d => {
                          const category = d.product.category;
                          // More aggressive truncation for long categories
                          return category.length > 16 ? category.substring(0, 16) + '...' : category;
                        });
                      
                      // Add click handler for selection
                      nodeEnter.on('click', (event, d) => {
                        event.stopPropagation();
                        setSelectedProduct(d.product);
                        setSelectedInterface(null); // Clear interface selection when product is selected
                      });
                      
                      // Add drag behavior
                      nodeEnter.call(d3.drag<SVGGElement, NodeData>()
                        .on('start', (event, d) => {
                          d.fx = d.x;
                          d.fy = d.y;
                        })
                        .on('drag', (event, d) => {
                          d.fx = event.x;
                          d.fy = event.y;
                          d.x = event.x;
                          d.y = event.y;
                          d3.select(event.sourceEvent.target.parentNode)
                            .attr('transform', `translate(${d.x},${d.y})`);
                        })
                        .on('end', (event, d) => {
                          d.fx = null;
                          d.fy = null;
                        })
                      );
                      
                      console.log(`🎨 First node DOM element created directly at center without simulation`);
                      
                      // Now update simulation with the node for future interactions
                      simulation.nodes([newNode]);
                    }
                    
                    // THEN set React state (this will trigger useEffect but simulation is already updated)
                    setNodes([newNode]);
                    setLinks([]);
                    
                    // Keep the first node fixed at center until expanded
                    // (Position will be released when user expands the node)
                    console.log(`� First node fixed at exact center with all forces disabled`);
                    
                    console.log(`✅ First product rendered immediately at center without any animation`);
                    
                    // Trigger updateVisualization to add expand indicators and other interactive elements
                    setTimeout(() => {
                      const updateVisualizationFunc = (simulation as any).updateVisualization;
                      if (updateVisualizationFunc) {
                        console.log(`🔄 Calling updateVisualization to add expand indicators for click`);
                        updateVisualizationFunc(new Set());
                      }
                    }, 0);
                  } else {
                    addProductToVisualization(product);
                  }
                }}
              >
                <div className="product-info">
                  <span className="product-name">{product.name}</span>
                  <span className="product-category">{product.category}</span>
                  <span className="product-team">{product.team}</span>
                </div>
                <div className="product-type-badge">
                  {product.type === 'internal' ? 'MSI' : 'EXT'}
                </div>
              </div>
            ))}
                </div>
              </div>
            )}
          </div>

          {/* Interfaces Panel */}
          <div className={`sidebar-panel interfaces-panel ${showInterfacesPanel ? 'expanded' : 'collapsed'}`}>
            <div className="panel-header" onClick={() => setShowInterfacesPanel(!showInterfacesPanel)}>
              {showInterfacesPanel ? (
                <>
                  <h3>Interfaces</h3>
                  <span className="panel-toggle">{"<<"}</span>
                </>
              ) : (
                <>
                  <h3 className="vertical-title">Interfaces</h3>
                  <span className="panel-toggle">{"+"}</span>
                </>
              )}
            </div>
            {showInterfacesPanel && (
              <div className="interface-controls">
                <input
                  type="text"
                  placeholder="Search interfaces..."
                  value={interfaceSearchTerm}
                  onChange={(e) => setInterfaceSearchTerm(e.target.value)}
                  className="interface-search-input"
                />
                <select
                  value={selectedProtocol}
                  onChange={(e) => setSelectedProtocol(e.target.value)}
                  className="protocol-filter"
                >
                  {protocols.map(protocol => (
                    <option key={protocol} value={protocol}>
                      {protocol === 'all' ? 'All Protocols' : protocol}
                    </option>
                  ))}
                </select>
                <select
                  value={selectedInterfaceStatus}
                  onChange={(e) => setSelectedInterfaceStatus(e.target.value)}
                  className="interface-status-filter"
                >
                  {interfaceStatuses.map(status => (
                    <option key={status} value={status}>
                      {status === 'all' ? 'All Statuses' : status}
                    </option>
                  ))}
                </select>
                <div className="interface-list">
                  {filteredInterfaces.map(iface => (
                    <div
                      key={iface.id}
                      className={`interface-item ${iface.status} ${selectedInterface?.id === iface.id ? 'selected' : ''}`}
                      onClick={() => {
                        // Create a pseudo-product that represents the interface for detail display (same as interface icon click)
                        const connectedProducts = getProductsConnectedByInterface(iface);
                        console.log(`🎯 Clicked interface: ${iface.name}`);
                        console.log(`🎯 Connected products:`, connectedProducts);
                        
                        const sourceProduct = connectedProducts[0];
                        const targetProduct = connectedProducts[1];
                        
                        const interfaceProduct: Product = {
                          id: iface.id,
                          name: iface.name,
                          category: 'Integration Interface',
                          team: `${sourceProduct?.team || 'Unknown'} & ${targetProduct?.team || 'Unknown'}`,
                          type: 'internal', // This field will be hidden for interfaces
                          status: iface.status === 'planned' ? 'development' : iface.status as 'active' | 'deprecated' | 'development',
                          productContact: sourceProduct && targetProduct ? `${sourceProduct.name} ↔ ${targetProduct.name}` : 'Interface connection',
                          engineeringContact: iface.description || 'Interface integration between systems',
                          // Store interface details for easy access
                          interfaceDetails: {
                            protocol: iface.protocol,
                            direction: iface.direction,
                            authentication: iface.authentication,
                            dataFormat: iface.dataFormat,
                            technicalDocumentationUrl: iface.technicalDocumentationUrl
                          }
                        };
                        
                        setSelectedProduct(interfaceProduct);
                        setSelectedInterface(null); // Clear interface selection to show in product details panel instead
                        
                        if (connectedProducts.length >= 2) {
                          // Add connected products to visualization with specific positioning
                          const product1 = connectedProducts[0];
                          const product2 = connectedProducts[1];
                          
                          // Position products in the center with some separation
                          const simWidth = (simulation as any)?.width || 1200;
                          const simHeight = (simulation as any)?.height || 800;
                          const centerX = simWidth / 2;
                          const centerY = simHeight / 2;
                          const offsetX = Math.min(220, simWidth * 0.18); // Responsive offset
                          
                          addProductToVisualization(product1, { x: centerX - offsetX/2, y: centerY }, false); // Don't auto-select
                          addProductToVisualization(product2, { x: centerX + offsetX/2, y: centerY }, false); // Don't auto-select
                          
                          // Find the connection that uses this interface
                          const connection = SAMPLE_CONNECTIONS.find(conn => 
                            conn.interface?.id === iface.id &&
                            ((conn.sourceProductId === product1.id && conn.targetProductId === product2.id) ||
                             (conn.sourceProductId === product2.id && conn.targetProductId === product1.id))
                          );
                          
                          if (connection) {
                            // Create the link between the products after a short delay to ensure nodes are added
                            setTimeout(() => {
                              setNodes(currentNodes => {
                                setLinks(prevLinks => {
                                  // Check if link already exists
                                  const linkExists = prevLinks.some(link => 
                                    (link.source.id === product1.id && link.target.id === product2.id) ||
                                    (link.source.id === product2.id && link.target.id === product1.id)
                                  );
                                  
                                  if (!linkExists) {
                                    // Find the actual node objects from current state
                                    const sourceNode = currentNodes.find(n => n.id === connection.sourceProductId);
                                    const targetNode = currentNodes.find(n => n.id === connection.targetProductId);
                                    
                                    if (sourceNode && targetNode) {
                                      const newLink: LinkData = {
                                        connection,
                                        source: sourceNode,
                                        target: targetNode
                                      };
                                      
                                      console.log(`🔗 Adding interface connection from click:`, newLink);
                                      return [...prevLinks, newLink];
                                    }
                                  }
                                  return prevLinks;
                                });
                                return currentNodes; // Return unchanged nodes
                              });
                            }, 100); // Small delay to ensure nodes are added first
                          }
                        } else {
                          // Fallback: add all connected products if we have a different number
                          connectedProducts.forEach((product: Product, index: number) => {
                            const simWidth = (simulation as any)?.width || 1200;
                            const simHeight = (simulation as any)?.height || 800;
                            const offsetX = index * Math.min(220, simWidth * 0.18); // Responsive spacing
                            const centerX = simWidth / 2;
                            const centerY = simHeight / 2;
                            addProductToVisualization(product, { x: centerX + offsetX, y: centerY });
                          });
                        }
                      }}
                      draggable
                      onDragStart={(e) => {
                        const dragData = {
                          type: 'interface',
                          interface: iface
                        };
                        e.dataTransfer.setData('application/json', JSON.stringify(dragData));
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                    >
                      <div className="interface-header">
                        <div className="interface-main">
                          <span className="interface-name">{iface.name}</span>
                          <div className="interface-metadata">
                            <span className="interface-protocol">{getProtocolDisplay(iface.protocol || 'API')}</span>
                            <span className={`interface-direction ${iface.direction}`}>
                              <span className="direction-icon">{getDirectionDisplay(iface.direction).icon}</span>
                              <span className="direction-text">{getDirectionDisplay(iface.direction).text}</span>
                            </span>
                          </div>
                        </div>
                        <span className={`interface-status ${iface.status}`}>{iface.status}</span>
                      </div>
                      {iface.description && (
                        <div className="interface-description">{iface.description}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="main-content">
          <div className="visualization-canvas">
            <svg
              ref={svgRef}
              width="100%"
              height="100%"
              className={isDragOver ? 'drag-over' : ''}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
                setIsDragOver(true);
              }}
              onDragLeave={(e) => {
                // Only set drag over to false if leaving the SVG itself
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  setIsDragOver(false);
                }
            }}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragOver(false);
              try {
                const dropData = e.dataTransfer.getData('application/json');
                if (dropData) {
                  const droppedItem = JSON.parse(dropData);
                  
                  // Check if it's an interface being dropped
                  if (droppedItem.type === 'interface' && droppedItem.interface) {
                    // For interface drops, we need to add the connected products
                    const interfaceObj = droppedItem.interface;
                    const connectedProducts = getProductsConnectedByInterface(interfaceObj);
                    
                    // Get SVG coordinates from the drop event
                    const svg = svgRef.current!;
                    const rect = svg.getBoundingClientRect();
                    const viewBox = svg.viewBox.baseVal;
                    
                    // Convert screen coordinates to SVG coordinates
                    const x = ((e.clientX - rect.left) / rect.width) * viewBox.width;
                    const y = ((e.clientY - rect.top) / rect.height) * viewBox.height;
                    
                    console.log(`🎯 Dropped interface at SVG coordinates: (${x}, ${y})`);
                    console.log(`🎯 Connected products:`, connectedProducts);
                    
                    if (connectedProducts.length >= 2) {
                      // Add connected products to visualization with specific positioning
                      const product1 = connectedProducts[0];
                      const product2 = connectedProducts[1];
                      
                      // Position products horizontally with some separation
                      const offsetX = 220;
                      addProductToVisualization(product1, { x: x - offsetX/2, y });
                      addProductToVisualization(product2, { x: x + offsetX/2, y });
                      
                      // Find the connection that uses this interface
                      const connection = SAMPLE_CONNECTIONS.find(conn => 
                        conn.interface?.id === interfaceObj.id &&
                        ((conn.sourceProductId === product1.id && conn.targetProductId === product2.id) ||
                         (conn.sourceProductId === product2.id && conn.targetProductId === product1.id))
                      );
                      
                      if (connection) {
                        // Create the link between the products after a short delay to ensure nodes are added
                        setTimeout(() => {
                          setNodes(currentNodes => {
                            setLinks(prevLinks => {
                              // Check if link already exists
                              const linkExists = prevLinks.some(link => 
                                (link.source.id === product1.id && link.target.id === product2.id) ||
                                (link.source.id === product2.id && link.target.id === product1.id)
                              );
                              
                              if (!linkExists) {
                                // Find the actual node objects from current state
                                const sourceNode = currentNodes.find(n => n.id === connection.sourceProductId);
                                const targetNode = currentNodes.find(n => n.id === connection.targetProductId);
                                
                                if (sourceNode && targetNode) {
                                  const newLink: LinkData = {
                                    connection,
                                    source: sourceNode,
                                    target: targetNode
                                  };
                                  
                                  console.log(`🔗 Adding interface connection:`, newLink);
                                  return [...prevLinks, newLink];
                                }
                              }
                              return prevLinks;
                            });
                            return currentNodes; // Return unchanged nodes
                          });
                        }, 100); // Small delay to ensure nodes are added first
                      }
                    } else {
                      // Fallback: add all connected products if we have a different number
                      connectedProducts.forEach((product: Product, index: number) => {
                        const offsetX = index * 220; // Spread products horizontally
                        addProductToVisualization(product, { x: x + offsetX, y });
                      });
                    }
                  }
                  // Handle product drops (original functionality)
                  else if (droppedItem.id && droppedItem.name) {
                    // This is a product drop
                    const product = droppedItem;
                    
                    // Get SVG coordinates from the drop event
                    const svg = svgRef.current!;
                    const rect = svg.getBoundingClientRect();
                    const viewBox = svg.viewBox.baseVal;
                    
                    // Convert screen coordinates to SVG coordinates
                    const x = ((e.clientX - rect.left) / rect.width) * viewBox.width;
                    const y = ((e.clientY - rect.top) / rect.height) * viewBox.height;
                    
                    console.log(`🎯 Dropped product at SVG coordinates: (${x}, ${y})`);
                    
                    addProductToVisualization(product, { x, y });
                  }
                }
              } catch (error) {
                console.error('Error parsing dropped data:', error);
              }
            }}
          />
          {showInstructions && (
            <div className="canvas-instructions">
              <button 
                className="close-instructions"
                onClick={() => setShowInstructions(false)}
                aria-label="Close instructions"
              >
                ×
              </button>
              <p><strong>Getting Started:</strong></p>
              <p>• <strong>Drag and drop</strong> products from the sidebar to the canvas</p>
              <p>• <strong>Click</strong> on products in the sidebar to add them to the center</p>
              <p>• <strong>Click or drag</strong> interfaces to WorkflowBuilder to view connected products</p>
              <p>• Click the <strong>+ button</strong> on nodes to expand their connections</p>
              <p>• Click the <strong>- button</strong> on nodes to collapse their connections</p>
              <p>• <strong>Drag nodes</strong> to rearrange the layout</p>
              <p>• <strong>Drag nodes back</strong> to the sidebar to remove them</p>
              <p>• Use <strong>mouse wheel</strong> to zoom in/out</p>
              <p>• Use <strong>filters above</strong> to narrow down the product list</p>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Only render bottom-details when there's content to show */}
      {(selectedInterface || selectedProduct) && (
        <div className="bottom-details">
          {/* Interface Details Panel */}
          {selectedInterface && (
            <div className="interface-details">
              <div className="details-header">
                <h3>{selectedInterface.name}</h3>
                <button 
                  className="close-details-btn"
                  onClick={() => setSelectedInterface(null)}
                  title="Close details panel"
                >
                  ×
                </button>
              </div>
              <div className="details-grid">
              <div className="detail-item">
                <strong>Protocol:</strong> {selectedInterface.protocol || 'Not specified'}
              </div>
              <div className="detail-item">
                <strong>Direction:</strong> {selectedInterface.direction}
              </div>
              <div className="detail-item">
                <strong>Status:</strong> {selectedInterface.status}
              </div>
              {selectedInterface.type && (
                <div className="detail-item">
                  <strong>Type:</strong> {selectedInterface.type}
                </div>
              )}
              {selectedInterface.team && (
                <div className="detail-item span-2">
                  <strong>Team:</strong> {selectedInterface.team}
                </div>
              )}
              {selectedInterface.authentication && (
                <div className="detail-item span-2">
                  <strong>Authentication:</strong> {selectedInterface.authentication}
                </div>
              )}
              {selectedInterface.dataFormat && (
                <div className="detail-item">
                  <strong>Data Format:</strong> {selectedInterface.dataFormat}
                </div>
              )}
              {selectedInterface.technicalDocumentationUrl && (
                <div className="detail-item">
                  <strong>Documentation:</strong> 
                  <a href={selectedInterface.technicalDocumentationUrl} target="_blank" rel="noopener noreferrer">
                    View Documentation
                  </a>
                </div>
              )}
              {selectedInterface.categories && selectedInterface.categories.length > 0 && (
                <div className="detail-item full-width">
                  <strong>Categories:</strong> {selectedInterface.categories.join(', ')}
                </div>
              )}
              {selectedInterface.description && (
                <div className="detail-item full-width">
                  <strong>Description:</strong> {selectedInterface.description}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Product Details Panel - Only show if no interface is selected */}
        {!selectedInterface && selectedProduct && (
          <div className="product-details">
            <div className="details-header">
              <h3>{selectedProduct.name}</h3>
              <button 
                className="close-details-btn"
                onClick={() => setSelectedProduct(null)}
                title="Close details panel"
              >
                ×
              </button>
            </div>
            <div className="details-grid">
              <div className="detail-item">
                <strong>Category:</strong> {selectedProduct.category}
              </div>
              <div className="detail-item">
                <strong>Team:</strong> {selectedProduct.team}
              </div>
              {/* Hide Type field for interfaces */}
              {selectedProduct.category !== 'Integration Interface' && (
                <div className="detail-item">
                  <strong>Type:</strong> {selectedProduct.type}
                </div>
              )}
              <div className="detail-item">
                <strong>Status:</strong> {selectedProduct.status}
              </div>
              {selectedProduct.productContact && (
                <div className="detail-item">
                  <strong>Product Contact:</strong> {selectedProduct.productContact}
                </div>
              )}
              {selectedProduct.engineeringContact && (
                <div className="detail-item">
                  <strong>Engineering Contact:</strong> {selectedProduct.engineeringContact}
                </div>
              )}
              
              {/* Show technical details for interfaces */}
              {selectedProduct.category === 'Integration Interface' && selectedProduct.interfaceDetails && (
                <>
                  <div className="detail-item">
                    <strong>Protocol:</strong> {selectedProduct.interfaceDetails.protocol || 'Not specified'}
                  </div>
                  <div className="detail-item">
                    <strong>Direction:</strong> {selectedProduct.interfaceDetails.direction || 'Not specified'}
                  </div>
                  <div className="detail-item">
                    <strong>Authentication:</strong> {selectedProduct.interfaceDetails.authentication || 'Not specified'}
                  </div>
                  <div className="detail-item">
                    <strong>Data Format:</strong> {selectedProduct.interfaceDetails.dataFormat || 'Not specified'}
                  </div>
                  {selectedProduct.interfaceDetails.technicalDocumentationUrl && (
                    <div className="detail-item">
                      <strong>Documentation:</strong>{' '}
                      <a 
                        href={selectedProduct.interfaceDetails.technicalDocumentationUrl}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="interface-doc-link"
                      >
                        View Technical Docs
                      </a>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="connections-info">
              {/* Show connections count differently for interfaces */}
              {selectedProduct.category === 'Integration Interface' ? (
                <span><strong>Connection Type:</strong> Interface</span>
              ) : (
                <span><strong>Connections:</strong> {getConnectionsForProduct(selectedProduct.id).length}</span>
              )}
              
              {/* Show interface information for external connections */}
              {selectedProduct.type === 'external' || selectedProduct.type === 'internal' ? (
                <div className="interfaces-info">
                  <strong>Interfaces:</strong>
                  {(() => {
                    const connectionsWithInterfaces = getConnectionsForProduct(selectedProduct.id)
                      .filter(conn => conn.interface);
                    
                    if (connectionsWithInterfaces.length > 0) {
                      return connectionsWithInterfaces.map(conn => (
                        <div 
                          key={conn.interface!.id} 
                          className="interface-item"
                          draggable
                          onDragStart={(e) => {
                            const dragData = {
                              type: 'interface',
                              interface: conn.interface!
                            };
                            e.dataTransfer.setData('application/json', JSON.stringify(dragData));
                            e.dataTransfer.effectAllowed = 'copy';
                          }}
                        >
                          <div className="interface-name">{conn.interface!.name}</div>
                          <div className="interface-details">
                            <span className={`interface-direction ${conn.interface!.direction}`}>
                              <span className="direction-icon">{getDirectionDisplay(conn.interface!.direction).icon}</span>
                              <span className="direction-text">{getDirectionDisplay(conn.interface!.direction).text}</span>
                            </span>
                            <span className="interface-protocol">{getProtocolDisplay(conn.interface!.protocol || 'API')}</span>
                            <span className={`interface-status ${conn.interface!.status}`}>
                              {conn.interface!.status}
                            </span>
                          </div>
                          {conn.interface!.description && (
                            <div className="interface-description">{conn.interface!.description}</div>
                          )}
                          {conn.interface!.technicalDocumentationUrl && (
                            <div className="interface-documentation">
                              <strong>Technical Documentation:</strong>{' '}
                              <a 
                                href={conn.interface!.technicalDocumentationUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="interface-doc-link"
                              >
                                View Documentation
                              </a>
                            </div>
                          )}
                        </div>
                      ));
                    } else if (selectedProduct.category === 'Integration Interface') {
                      return (
                        <div className="interface-item">
                          <div className="interface-name">Default Interface</div>
                          <div className="interface-details">
                            <span className="interface-direction bidirectional">
                              <span className="direction-icon">{getDirectionDisplay('bidirectional').icon}</span>
                              <span className="direction-text">{getDirectionDisplay('bidirectional').text}</span>
                            </span>
                            <span className="interface-protocol">API</span>
                            <span className="interface-status active">active</span>
                          </div>
                          <div className="interface-description">Standard integration interface</div>
                        </div>
                      );
                    } else {
                      return <span className="no-interfaces">No specific interfaces defined</span>;
                    }
                  })()}
                </div>
              ) : null}
            </div>
          </div>
        )}
        </div>
      )}
    </div>
  );
};

export default IntegrationVisualization;
